import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import type { LibraryItem } from '@/shared/contracts';
import { mmss } from '../lib/format';
import { currentModule, moduleState, upNext, type QueueEntry } from '../lib/learning';
import { PRIVATE_PREFIX, readProgressDraft, writeDraft } from '../lib/private-state';
import { putProgress, useItem, useLibrary, useMe, useSession } from '../lib/queries';
import { useTenant } from '../lib/tenant-context';
import { chapterAt } from './ChapterStrip';
import { SegmentBar } from './SegmentBar';

/*
 * The persistent player bar (design pass 3, item 6; Brendon 2026-09-22): on every signed-in screen,
 * the current or next module and the place the server holds for it, one play control, and the
 * audio queue as "Up next". Honest by rule: without a recording the play control is disabled and
 * says "Narration not recorded yet"; it never plays a sound, never moves a clock and never shows
 * an elapsed time it does not have. With a recording it plays that recording through the item's
 * own audio route. No module has one today (`content_items.audio_key` is empty in the content).
 */

export type PlayerBarState =
  | { status: 'loading' }
  | { status: 'error' }
  | { status: 'ready'; module: LibraryItem | null; queue: QueueEntry[]; readerHasAudio: boolean };

/** Whether the direct learning reader open at this route owns a real recording. */
export function readerHasRecordedAudio(pathname: string, library: LibraryItem[]): boolean {
  const match = /^\/learn\/([^/]+)$/.exec(pathname);
  if (!match?.[1]) return false;
  let id: string;
  try {
    id = decodeURIComponent(match[1]);
  } catch {
    return false;
  }
  return library.some((item) => item.id === id && item.has_audio);
}

/** The bar's data: the module the learner is on and the chapters still to come, from server state. */
export function usePlayerBar(enabled: boolean): PlayerBarState | null {
  const { pathname } = useLocation();
  const me = useMe(enabled);
  const library = useLibrary(enabled);
  if (!enabled) return null;
  if (library.isError || me.isError) return { status: 'error' };
  if (!library.data || !me.data) return { status: 'loading' };
  const module = currentModule(library.data, me.data.continue_item);
  return {
    status: 'ready',
    module,
    queue: upNext(library.data, module),
    readerHasAudio: readerHasRecordedAudio(pathname, library.data),
  };
}

/**
 * What the play control may do. `none`: every module is complete. `not-recorded`: disabled, and
 * says so. `on-page`: the module's own page owns its player, so the bar does not start a second
 * one. `playable`: a recording exists and the bar plays it.
 */
export type PlayControl = 'none' | 'not-recorded' | 'on-page' | 'playable';

export function playControl(
  module: LibraryItem | null,
  onOwnPage: boolean,
  readerHasAudio = false,
): PlayControl {
  if (!module) return 'none';
  if (!module.has_audio) return 'not-recorded';
  return onOwnPage || readerHasAudio ? 'on-page' : 'playable';
}

export const CONTROL_NOTE: Record<Exclude<PlayControl, 'playable'>, string> = {
  none: 'Every module is complete',
  'not-recorded': 'Narration not recorded yet',
  'on-page': 'Narration plays in the player on this page',
};

const PLAY_ICON = (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M8 5.14v13.72a.5.5 0 0 0 .77.42l10.5-6.86a.5.5 0 0 0 0-.84L8.77 4.72a.5.5 0 0 0-.77.42Z" />
  </svg>
);
const PAUSE_ICON = (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M7 4h3v16H7zM14 4h3v16h-3z" />
  </svg>
);

const DISC =
  'inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full transition-colors duration-150 ease-ea rail:h-12 rail:w-12';

/**
 * The play control with nothing to play. It stays in the tab order, so a screen reader meets it
 * and hears why, but it is `aria-disabled` and does nothing when pressed.
 */
function SilentPlay({ noteId }: { noteId: string }) {
  return (
    <button
      type="button"
      aria-label="Play narration"
      aria-disabled="true"
      aria-describedby={noteId}
      data-play-control="disabled"
      onClick={(event) => event.preventDefault()}
      className={`${DISC} cursor-not-allowed bg-chip text-inkFaint`}
    >
      {PLAY_ICON}
    </button>
  );
}

/**
 * A recording that exists, played through the item's audio route from the place the server holds,
 * with its real elapsed and remaining time. Pausing or finishing saves the place.
 */
function RecordedPlay({
  module,
  noteId,
  onTime,
  onProgressError,
}: {
  module: LibraryItem;
  noteId: string;
  onTime: (moduleId: string, time: { at: number; total: number } | null) => void;
  onProgressError: (moduleId: string, message: string | null) => void;
}) {
  const item = useItem(module.id);
  const session = useSession();
  const tenant = useTenant();
  const audio = useRef<HTMLAudioElement | null>(null);
  // The parent keys this component by module id. Keep that identity's source fields stable so a
  // background library refetch cannot tear down and pause an otherwise unchanged recording.
  const [identity] = useState(() => ({
    id: module.id,
    chapters: module.chapters,
    progress: module.progress,
  }));
  const latest = useRef({
    position_s: identity.progress?.position_s ?? 0,
    chapter_index: identity.progress?.chapter_index ?? 0,
  });
  const [playing, setPlaying] = useState(false);
  const [failed, setFailed] = useState(false);
  const src = item.data?.body.audio_url ?? null;
  const userId = session.data?.user?.id ?? null;
  const storageKey = userId ? `${PRIVATE_PREFIX}${tenant.slug}:${userId}:reading:${identity.id}` : null;
  const timed = identity.chapters.length > 0 && identity.chapters.every((c) => c.start_s !== null);

  useEffect(() => {
    const el = audio.current;
    if (!el) return;
    // A detail refetch may replace the recording URL without changing modules. Treat each source
    // as a fresh media load while keeping the module's latest recoverable position.
    let seeded = false;
    const ownsSource = () => el.getAttribute('src') === src;
    const position = () => ({
      position_s: Math.max(0, Math.floor(el.currentTime)),
      chapter_index: timed ? chapterAt(identity.chapters, el.currentTime) : latest.current.chapter_index,
    });
    const record = () => {
      // Changing src can emit a zero-valued timeupdate before the new metadata arrives. Ignore it
      // so it cannot overwrite the position held by the previous source.
      if (!seeded || !ownsSource()) return;
      latest.current = position();
      if (storageKey && !writeDraft(storageKey, latest.current))
        onProgressError(identity.id, 'Your place could not be kept on this browser.');
    };
    const report = () => {
      record();
      if (!seeded || !ownsSource()) return;
      onTime(identity.id, Number.isFinite(el.duration) ? { at: el.currentTime, total: el.duration } : null);
    };
    const seed = () => {
      if (seeded || !ownsSource()) return;
      seeded = true;
      const recovery = storageKey ? readProgressDraft(storageKey) : null;
      if (recovery) latest.current = recovery;
      const saved = recovery?.position_s ?? latest.current.position_s;
      if (saved > 0 && saved < el.duration) el.currentTime = saved;
      onTime(identity.id, Number.isFinite(el.duration) ? { at: el.currentTime, total: el.duration } : null);
    };
    const save = async () => {
      if (!userId) return;
      record();
      const sent = latest.current;
      try {
        await putProgress(identity.id, userId, sent);
        if (storageKey && JSON.stringify(readProgressDraft(storageKey)) === JSON.stringify(sent))
          writeDraft(storageKey, null);
        onProgressError(identity.id, null);
      } catch {
        onProgressError(identity.id, 'Your place has not been saved to your account.');
      }
    };
    const onPlay = () => setPlaying(true);
    const onPause = () => {
      setPlaying(false);
      void save();
    };
    const onError = () => {
      setPlaying(false);
      setFailed(true);
    };
    el.addEventListener('loadedmetadata', seed);
    el.addEventListener('timeupdate', report);
    el.addEventListener('play', onPlay);
    el.addEventListener('pause', onPause);
    el.addEventListener('error', onError);
    if (el.readyState >= HTMLMediaElement.HAVE_METADATA) seed();
    return () => {
      el.removeEventListener('loadedmetadata', seed);
      el.removeEventListener('timeupdate', report);
      el.removeEventListener('play', onPlay);
      el.removeEventListener('pause', onPause);
      el.removeEventListener('error', onError);
      if (!el.paused) {
        // A route or module identity change must pause the old recording and keep its latest place.
        void save();
        el.pause();
      }
      onTime(identity.id, null);
    };
  }, [src, userId, timed, storageKey, identity, onTime, onProgressError]);

  const toggle = () => {
    const el = audio.current;
    if (!el) return;
    setFailed(false);
    if (el.paused) void el.play().catch(() => setFailed(true));
    else el.pause();
  };

  return (
    <>
      <button
        type="button"
        aria-label={playing ? 'Pause narration' : 'Play narration'}
        aria-pressed={playing}
        aria-describedby={noteId}
        data-play-control="recording"
        disabled={!src}
        onClick={toggle}
        className={`${DISC} bg-ink text-bgElevated hover:bg-inkMuted disabled:opacity-50`}
      >
        {playing ? PAUSE_ICON : PLAY_ICON}
      </button>
      {src && <audio ref={audio} src={src} preload="metadata" data-player-bar-audio="" />}
      {failed && (
        <span role="alert" className="sr-only">
          Narration is unavailable. Try again.
        </span>
      )}
    </>
  );
}

/** "Up next": the remaining chapters in curriculum order, in a dialog that keeps focus to itself. */
function QueueDialog({
  queue,
  open,
  onClose,
  titleId,
}: {
  queue: QueueEntry[];
  open: boolean;
  onClose: () => void;
  titleId: string;
}) {
  const dialog = useRef<HTMLDialogElement>(null);
  const close = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    const el = dialog.current;
    if (!el) return;
    if (open && !el.open) {
      el.showModal();
      close.current?.focus();
    } else if (!open && el.open) el.close();
  }, [open]);
  const groups: { module: LibraryItem; chapters: QueueEntry['chapter'][] }[] = [];
  for (const entry of queue) {
    const last = groups.at(-1);
    if (last?.module.id === entry.module.id) last.chapters.push(entry.chapter);
    else groups.push({ module: entry.module, chapters: [entry.chapter] });
  }
  return (
    <dialog
      ref={dialog}
      aria-labelledby={titleId}
      onClose={onClose}
      onCancel={onClose}
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
      className="m-auto max-h-[min(640px,calc(100dvh-32px))] w-[min(560px,calc(100vw-32px))] overflow-hidden rounded-[14px] border border-rule bg-panel p-0 text-ink shadow-md backdrop:bg-[rgba(12,13,15,0.5)]"
    >
      <div className="flex max-h-[inherit] flex-col">
        <div className="flex items-start justify-between gap-3 border-b border-rule px-5 py-4">
          <div className="min-w-0">
            <h2 id={titleId} className="t-heading text-ink">
              Up next · <span className="mono">{queue.length}</span>
            </h2>
            <p className="mt-1 text-[13px] text-inkMuted">
              Remaining chapters, in course order. Each opens on its module page.
            </p>
          </div>
          <button
            ref={close}
            type="button"
            onClick={onClose}
            className="inline-flex h-11 shrink-0 items-center rounded-[10px] border border-rule2 bg-panel px-3.5 text-[14px] font-medium text-ink hover:border-accent hover:text-accentText"
          >
            Close
          </button>
        </div>
        <div className="overflow-y-auto px-5 pb-5">
          {groups.length === 0 ? (
            <p className="pt-4 text-[14px] text-inkMuted">
              Every module is complete. Review any chapter from Learn.
            </p>
          ) : (
            groups.map(({ module, chapters }) => (
              <section
                key={module.id}
                aria-label={module.title}
                className="border-b border-line2 py-3 last:border-b-0"
              >
                <div className="flex items-center justify-between gap-3">
                  <h3 className="text-[15px] font-medium text-ink">{module.title}</h3>
                  <Link
                    to={`/learn/${encodeURIComponent(module.id)}`}
                    onClick={onClose}
                    className="inline-flex min-h-11 shrink-0 items-center text-[14px] font-medium text-accentText no-underline hover:underline"
                  >
                    Open module<span className="sr-only"> {module.title}</span>
                  </Link>
                </div>
                <ol className="m-0 list-none p-0">
                  {chapters.map((chapter) => (
                    <li key={chapter.index} className="flex gap-3 py-1 text-[14px] text-inkMuted">
                      <span className="mono w-12 shrink-0 text-inkFaint">
                        Ch {String(chapter.index + 1).padStart(2, '0')}
                      </span>
                      <span className="min-w-0">{chapter.title}</span>
                    </li>
                  ))}
                </ol>
              </section>
            ))
          )}
        </div>
      </div>
    </dialog>
  );
}

/**
 * The bar itself. On a phone it floats above the tab bar; on a desk it runs along the bottom of the
 * content column, as the mockup draws its player.
 */
export function PlayerBar({ bar }: { bar: PlayerBarState }) {
  const { pathname } = useLocation();
  const noteId = useId();
  const titleId = useId();
  // The queue is open on the route it was opened from, so leaving that route closes it.
  const [openOn, setOpenOn] = useState<string | null>(null);
  const open = openOn === pathname;
  const [time, setTime] = useState<{ moduleId: string; at: number; total: number } | null>(null);
  const [progressError, setProgressError] = useState<{ moduleId: string; message: string } | null>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const module = bar.status === 'ready' ? bar.module : null;
  const queue = bar.status === 'ready' ? bar.queue : [];
  const own = module ? `/learn/${encodeURIComponent(module.id)}` : null;
  const onOwnPage = own !== null && pathname === own;
  const control = bar.status === 'ready' ? playControl(module, onOwnPage, bar.readerHasAudio) : 'none';
  const state = module ? moduleState(module) : null;

  const reportTime = useCallback(
    (moduleId: string, next: { at: number; total: number } | null) =>
      setTime(next ? { moduleId, ...next } : null),
    [],
  );
  const reportProgressError = useCallback((moduleId: string, message: string | null) => {
    setProgressError(message ? { moduleId, message } : null);
  }, []);
  const activeProgressError =
    progressError && progressError.moduleId === module?.id ? progressError.message : null;
  const activeTime = time && time.moduleId === module?.id ? time : null;

  const closeQueue = () => {
    setOpenOn(null);
    trigger.current?.focus();
  };

  const note =
    bar.status === 'loading'
      ? 'Loading your place'
      : bar.status === 'error'
        ? 'Your place could not be loaded'
        : control === 'playable'
          ? activeProgressError
            ? activeProgressError
            : activeTime
              ? `${mmss(activeTime.at)} · ${mmss(Math.max(0, activeTime.total - activeTime.at))} left`
              : 'Narration recorded'
          : CONTROL_NOTE[control];

  const where =
    !module || !state
      ? 'Review any module from Learn'
      : state.place !== null
        ? `Chapter ${state.place + 1} of ${state.total} · about ${state.minutesLeft} min left`
        : `Chapter 1 of ${state.total} · ${state.verb === 'Start' ? 'not started' : state.status}`;

  return (
    <div
      data-player-bar=""
      className="fixed inset-x-0 bottom-14 z-40 px-2 pb-2 rail:bottom-0 rail:left-[264px] rail:px-0 rail:pb-0"
      style={{ marginBottom: 'env(safe-area-inset-bottom)' }}
    >
      <section
        aria-label="Narration and queue"
        className="panel mx-auto flex h-[68px] w-full max-w-2xl items-center gap-3 overflow-hidden px-3 shadow-md sm:px-4 rail:h-[76px] rail:max-w-none rail:gap-4 rail:rounded-none rail:border-x-0 rail:border-b-0 rail:px-8 rail:shadow-none"
      >
        {control === 'playable' && module ? (
          <RecordedPlay
            key={module.id}
            module={module}
            noteId={noteId}
            onTime={reportTime}
            onProgressError={reportProgressError}
          />
        ) : (
          <SilentPlay noteId={noteId} />
        )}
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          {module ? (
            <Link
              to={own ?? '/library'}
              className="flex min-h-11 min-w-0 flex-col justify-center no-underline hover:text-accentText"
            >
              <span className="truncate text-[14px] font-medium leading-tight text-ink">
                {module.title}
                <span className="hidden font-normal text-inkFaint rail:inline"> · {where}</span>
              </span>
              <span
                id={noteId}
                className="truncate text-[12.5px] leading-tight text-inkMuted"
                data-player-note=""
              >
                {note}
                <span className="rail:hidden"> · {where}</span>
              </span>
            </Link>
          ) : (
            <Link
              to="/library"
              className="flex min-h-11 min-w-0 flex-col justify-center no-underline hover:text-accentText"
            >
              <span className="truncate text-[14px] font-medium leading-tight text-ink">
                {bar.status === 'ready' ? 'Every module is complete' : 'Player'}
              </span>
              <span
                id={noteId}
                className="truncate text-[12.5px] leading-tight text-inkMuted"
                data-player-note=""
              >
                {note}
              </span>
            </Link>
          )}
          {state && (
            <SegmentBar
              thin
              total={state.total}
              complete={state.complete}
              place={state.place}
              className="w-full"
            />
          )}
        </div>
        {state && (
          <span className="mono hidden shrink-0 whitespace-nowrap text-[13px] text-inkMuted rail:inline">
            {state.place !== null ? `about ${state.minutesLeft} min left` : `about ${state.minutes} min`}
          </span>
        )}
        <button
          ref={trigger}
          type="button"
          aria-haspopup="dialog"
          aria-expanded={open}
          aria-label={`Up next, ${queue.length} ${queue.length === 1 ? 'chapter' : 'chapters'}`}
          disabled={bar.status !== 'ready'}
          onClick={() => setOpenOn(pathname)}
          className="inline-flex h-11 shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border border-rule2 bg-panel px-3.5 text-[13px] font-medium text-ink transition-colors duration-150 ease-ea hover:border-accent hover:text-accentText disabled:opacity-50"
        >
          Up next<span aria-hidden="true"> ·</span> <span className="mono">{queue.length}</span>
        </button>
      </section>
      {bar.status === 'ready' && (
        <QueueDialog queue={queue} open={open} onClose={closeQueue} titleId={titleId} />
      )}
    </div>
  );
}
